import { apiRequest } from './axiosConfig';

// AdminVideoTemplate type definition
export interface AdminVideoTemplate {
  id: number;
  category: string;
  name: string;
  icon?: string;
  description?: string;
  prompt_template: string;
  gradient?: string;
  thumbnail_url?: string | null;
  sample_video_key?: string | null;
  sample_poster_key?: string | null;
  sample_video_url?: string | null;
  sample_poster_url?: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VideoTemplateCreateRequest {
  category: string;
  name: string;
  icon?: string;
  description?: string;
  prompt_template: string;
  gradient?: string;
  /** null を送るとサムネイルを外す */
  thumbnail_url?: string | null;
  /** null を送るとサンプルを外す。送らなければ変更しない */
  sample_video_key?: string | null;
  sample_poster_key?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

export interface VideoTemplateUpdateRequest {
  category?: string;
  name?: string;
  icon?: string;
  description?: string;
  prompt_template?: string;
  gradient?: string;
  /** null を送るとサムネイルを外す */
  thumbnail_url?: string | null;
  /** null を送るとサンプルを外す。送らなければ変更しない */
  sample_video_key?: string | null;
  sample_poster_key?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

interface VideoTemplatesResponse {
  success: boolean;
  data: AdminVideoTemplate[];
}

interface VideoTemplateResponse {
  success: boolean;
  data: AdminVideoTemplate;
  message?: string;
}

interface VideoTemplateDeleteResponse {
  success: boolean;
  message: string;
}

interface CategoriesResponse {
  success: boolean;
  data: string[];
}

/**
 * Admin video template API service
 */
const adminVideoTemplateService = {
  /**
   * Get all video templates (admin)
   */
  async getAllTemplates(): Promise<AdminVideoTemplate[]> {
    const response = await apiRequest.get<VideoTemplatesResponse>('admin/video-templates');
    return response.data;
  },

  /**
   * Get a single video template
   */
  async getTemplate(id: number): Promise<AdminVideoTemplate> {
    const response = await apiRequest.get<VideoTemplateResponse>(`admin/video-templates/${id}`);
    return response.data;
  },

  /**
   * Create a new video template
   */
  async createTemplate(data: VideoTemplateCreateRequest): Promise<AdminVideoTemplate> {
    const response = await apiRequest.post<VideoTemplateResponse>('admin/video-templates', data);
    return response.data;
  },

  /**
   * Update an existing video template
   */
  async updateTemplate(id: number, data: VideoTemplateUpdateRequest): Promise<AdminVideoTemplate> {
    const response = await apiRequest.put<VideoTemplateResponse>(`admin/video-templates/${id}`, data);
    return response.data;
  },

  /**
   * Delete a video template
   */
  async deleteTemplate(id: number): Promise<void> {
    await apiRequest.delete<VideoTemplateDeleteResponse>(`admin/video-templates/${id}`);
  },

  /**
   * Toggle video template active/inactive
   */
  async toggleTemplate(id: number): Promise<AdminVideoTemplate> {
    const response = await apiRequest.put<VideoTemplateResponse>(`admin/video-templates/${id}/toggle`);
    return response.data;
  },

  /**
   * Update sort order for multiple templates
   */
  async updateOrder(templates: { id: number; sort_order: number }[]): Promise<void> {
    await apiRequest.put('admin/video-templates/order', { templates });
  },

  /**
   * 動画スタイルのサンプル動画・ポスター画像・サムネイル画像をアップロードし、保存先キーと表示用 URL を返す
   */
  async uploadSample(type: 'video' | 'poster' | 'thumbnail', file: Blob, fileName: string): Promise<{ key: string; url: string }> {
    const formData = new FormData();
    formData.append('type', type);
    formData.append('file', file, fileName);
    const response = await apiRequest.post<{ success: boolean; data: { key: string; url: string } }>(
      'admin/video-templates/upload-sample',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  /**
   * Get distinct categories
   */
  async getCategories(): Promise<string[]> {
    const response = await apiRequest.get<CategoriesResponse>('admin/video-templates/categories');
    return response.data;
  },
};

export default adminVideoTemplateService;
